import clsx from 'clsx'

import type { ChatScopeId } from '../../lib/data'

// ============================================================================
// The small repeated pieces of the Chat screen.
// ============================================================================

/**
 * SCOPE PILL COLOURS, from docs/design/handoff/README.md §10 verbatim:
 *
 *   NEU C61 / School  info-fill    / info
 *   Interests         accent-fill  / accent
 *   Journal           warning-fill / warning-2
 *   Learning          teal-fill    / teal
 *   Everything        fill-2       / ink-3
 *
 * (Token names, not the README's literals: no raw hex may appear anywhere in apps/web/src outside
 * index.css — the ship gate's brand audit greps for it, and a hex in a comment drifts from the
 * token exactly as fast as one in a class name.)
 *
 * A course pill takes School's blue — the README lists `NEU C61 / School` as ONE row, which is
 * the design saying a course is a kind of school rather than a colour of its own. Four courses
 * in four hues would turn the thread list into a legend.
 *
 * Fill and ink are kept apart rather than in one class string, because the active row needs the
 * ink without the fill (see `ScopePill`).
 */
const SCOPE_PILL: Record<string, { fill: string; ink: string }> = {
  everything: { fill: 'bg-fill-2', ink: 'text-ink-3' },
  journal: { fill: 'bg-warning-fill', ink: 'text-warning-2' },
  school: { fill: 'bg-info-fill', ink: 'text-info' },
  interests: { fill: 'bg-accent-fill', ink: 'text-accent' },
  learning: { fill: 'bg-teal-fill', ink: 'text-teal' },
}

/** A `course:<id>` scope is not in the table and falls through to School's blue, by design. */
const pillOf = (scopeId: ChatScopeId) => SCOPE_PILL[scopeId] ?? SCOPE_PILL.school!

/**
 * The pill on a thread-list row.
 *
 * `onLift` is the ACTIVE row, and it is not decoration: that row's own ground is `fill`, so a pill
 * tinted lighter than its row would read as a hole punched in it. The mockup flips the pill to
 * `surface` and keeps the ink — read off the same NEU C61 pill, drawn `info-fill` on 11b's
 * inactive row and white on 11a's active one.
 */
export function ScopePill({
  scopeId,
  label,
  onLift,
}: {
  scopeId: ChatScopeId
  label: string
  onLift?: boolean
}) {
  const { fill, ink } = pillOf(scopeId)
  return (
    <span
      className={clsx(
        'flex-none rounded-pill px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.6]',
        onLift ? 'bg-surface' : fill,
        ink,
      )}
    >
      {label}
    </span>
  )
}

/** The 5px hue dot in the composer's scope chip. Keyed by the scope's DOMAIN, not its id. */
const SCOPE_DOT: Record<string, string> = {
  everything: 'bg-muted',
  journal: 'bg-journal',
  school: 'bg-school',
  interests: 'bg-interests',
  learning: 'bg-learning',
}

export const scopeDotClass = (domain: string) => SCOPE_DOT[domain] ?? SCOPE_DOT.school!
